import { useCallback, useState } from "react";
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import { ContentCopyOutlined, OpenInNewOutlined, PublicOutlined } from "@mui/icons-material";
import { useCookies } from "react-cookie";

import findTransparencyPortalLink from "../resources/Api/transparencyPortal/findTransparencyPortalLink";
import regenerateTransparencyPortalLink from "../resources/Api/transparencyPortal/regenerateTransparencyPortalLink";
import { TRANSPARENCY_PORTAL_PATH } from "../pages/transparencyPortal";

const buildUrl = (linkToken: string) => `${window.location.origin}${TRANSPARENCY_PORTAL_PATH}${linkToken}`;

/**
 * TransparencyPortalLinkButton - Botão do gestor que exibe o link público do portal da transparência.
 * A API só responde para o gestor principal de clientes com a flag usa_link_portaltransparencia,
 * ou para o dev da administradora, informando o cliente selecionado em customerId.
 * @component
 */
const TransparencyPortalLinkButton = ({ customerId }: { customerId?: number }) => {
    const [cookies] = useCookies(["token"]);

    const [open, setOpen] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [confirmRegenerate, setConfirmRegenerate] = useState<boolean>(false);
    const [url, setUrl] = useState<string>("");
    const [message, setMessage] = useState<string>("");

    const getToken = useCallback(() => cookies.token || sessionStorage.getItem("token") || "", [cookies.token]);

    const handleOpen = useCallback(async () => {
        setOpen(true);
        setMessage("");
        setConfirmRegenerate(false);
        setLoading(true);
        try {
            const { data } = await findTransparencyPortalLink(getToken(), customerId);
            setUrl(buildUrl(data.token));
        } catch {
            setUrl("");
            setMessage("Não foi possível obter o link. Tente novamente.");
        } finally {
            setLoading(false);
        }
    }, [getToken, customerId]);

    const handleRegenerate = useCallback(async () => {
        setLoading(true);
        setMessage("");
        try {
            const { data } = await regenerateTransparencyPortalLink(getToken(), customerId);
            setUrl(buildUrl(data.token));
            setMessage("Novo link gerado. O link anterior não funciona mais.");
        } catch {
            setMessage("Não foi possível gerar um novo link. Tente novamente.");
        } finally {
            setConfirmRegenerate(false);
            setLoading(false);
        }
    }, [getToken, customerId]);

    const handleCopy = useCallback(async () => {
        try {
            await navigator.clipboard.writeText(url);
            setMessage("Link copiado.");
        } catch {
            setMessage("Não foi possível copiar, selecione o link e copie manualmente.");
        }
    }, [url]);

    return (
        <>
            <Tooltip title="Link do portal da transparência" arrow placement="left">
                <IconButton aria-label="Link do portal da transparência" onClick={handleOpen}>
                    <PublicOutlined sx={{ color: open ? "#00b700" : "#ffff" }} />
                </IconButton>
            </Tooltip>

            <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm" sx={{ zIndex: 100000 }}>
                <DialogTitle>Link do portal da transparência</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                        Qualquer pessoa com este link vê apenas o mapa com a placa e a localização dos veículos,
                        sem login e sem outras informações. O link não expira.
                    </Typography>

                    {loading ? (
                        <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                            <CircularProgress size={28} />
                        </Box>
                    ) : url ? (
                        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                            <TextField
                                value={url}
                                fullWidth
                                size="small"
                                InputProps={{ readOnly: true }}
                                onFocus={(e) => e.target.select()}
                            />
                            <Tooltip title="Copiar" arrow>
                                <IconButton aria-label="Copiar link" onClick={handleCopy}>
                                    <ContentCopyOutlined />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Abrir" arrow>
                                <IconButton aria-label="Abrir link" onClick={() => window.open(url, "_blank", "noopener,noreferrer")}>
                                    <OpenInNewOutlined />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    ) : null}

                    {message && (
                        <Typography variant="body2" sx={{ mt: 1.5 }}>
                            {message}
                        </Typography>
                    )}

                    {confirmRegenerate && !loading && (
                        <Typography variant="body2" color="error" sx={{ mt: 1.5 }}>
                            Gerar um novo link desativa o atual. Quem usa o link antigo perde o acesso. Confirmar?
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    {confirmRegenerate ? (
                        <>
                            <Button onClick={() => setConfirmRegenerate(false)} disabled={loading}>Cancelar</Button>
                            <Button color="error" onClick={handleRegenerate} disabled={loading}>Gerar novo link</Button>
                        </>
                    ) : (
                        <>
                            <Button color="error" onClick={() => setConfirmRegenerate(true)} disabled={loading || !url}>
                                Gerar novo link
                            </Button>
                            <Button onClick={() => setOpen(false)}>Fechar</Button>
                        </>
                    )}
                </DialogActions>
            </Dialog>
        </>
    );
};

export default TransparencyPortalLinkButton;
